import { FC, useCallback, useEffect, useState } from 'react';
import { AuthenticationResultStatus } from './AuthorizeService';
import { QueryParameterNames, LogoutActions, ApplicationPaths } from './ApiAuthorizationConstants';
import Loader from 'components/Loader';
import useAuth from 'hooks/useAuth';

type LogoutProps = {
    action: string;
};

// The main responsibility of this component is to handle the user's logout process.
// This is the starting point for the logout process, which is usually initiated when a
// user clicks on the logout button on the LoginMenu component.
const Logout: FC<LogoutProps> = ({ action }: LogoutProps) => {
    const [message, setMessage] = useState('');
    const { authService } = useAuth();

    const logout = useCallback(async () => {
        const isAuthenticated = await authService?.isAuthenticated();
        if (isAuthenticated) {
            await authService?.removeUser();

            if (!ApplicationPaths.ApiLogoutUrl) ApplicationPaths.ApiLogoutUrl = '/';
            window.location.replace(ApplicationPaths.ApiLogoutUrl);
        } else {
            setMessage('You successfully logged out!');
        }
    }, [authService]);

    const getReturnUrl = (state: { returnUrl: string }) => {
        const params = new URLSearchParams(window.location.search);
        const fromQuery = params.get(QueryParameterNames.ReturnUrl);
        if (fromQuery && !fromQuery.startsWith(`${window.location.origin}/`)) {
            // This is an extra check to prevent open redirects.
            throw new Error('Invalid return url. The return url needs to have the same origin as the current page.');
        }
        return (state && state.returnUrl) || fromQuery || `${window.location.origin}${ApplicationPaths.LoggedOut}`;
    };

    const navigateToReturnUrl = (returnUrl: string) => {
        return window.location.replace(returnUrl);
    };

    useEffect(() => {
        const processLogoutCallback = async () => {
            const url = window.location.href;
            const result = await authService?.completeSignOut(url);
            switch (result.status) {
                case AuthenticationResultStatus.Redirect:
                    // There should not be any redirects as the only time completeAuthentication finishes
                    // is when we are doing a redirect sign in flow.
                    throw new Error('Should not redirect.');
                case AuthenticationResultStatus.Success:
                    await navigateToReturnUrl(getReturnUrl(result.state));
                    break;
                case AuthenticationResultStatus.Fail:
                    setMessage(result.message);
                    break;
                default:
                    throw new Error('Invalid authentication result status.');
            }
        };

        switch (action) {
            case LogoutActions.Logout:
                console.log(window.history);
                // navigate() from react-router-dom v6 wrap states in usr
                if (window.history.state.usr.local) {
                    logout();
                } else {
                    // This prevents regular links to <app>/authentication/logout from triggering a logout
                    setMessage('The logout was not initiated from within the page.');
                }
                localStorage.setItem('nextHttpRequestNoCache', 'true');
                break;
            case LogoutActions.LogoutCallback:
                processLogoutCallback();
                break;
            case LogoutActions.LoggedOut:
                setMessage('You successfully logged out!');
                break;
            default:
                throw new Error(`Invalid action '${action}'`);
        }
    }, [action, authService, logout]);

    if (message) {
        return <div>{message}</div>;
    } else {
        switch (action) {
            case LogoutActions.Logout:
            case LogoutActions.LogoutCallback:
            case LogoutActions.LoggedOut:
                return <Loader />;
            default:
                throw new Error(`Invalid action '${action}'`);
        }
    }
};

export default Logout;
